import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2,
  Search,
  ExternalLink,
  ImageIcon,
  X,
  Building2,
  Trash2,
  Plus,
  RefreshCw,
  ChevronDown,
  Calendar,
  Megaphone,
  Library,
  Instagram,
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/facebook-ads-library';
const RADAR_API = 'https://api.glowytics.com/api/facebook-ads-library-radar';
const DEBOUNCE = 420;
const MIN_Q = 2;

/* ── Brand ID helper ─────────────────────────────────────────────── */

const getBrandId = () => {
  try { const c = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId'); if (c) return c; } catch (_) {}
  for (const k of ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id']) { try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {} }
  for (const k of ['selectedBrand', 'currentBrand', 'activeBrand']) { try { const r = localStorage.getItem(k) || sessionStorage.getItem(k); if (r) { const o = JSON.parse(r); if (o?.id) return o.id; } } catch (_) {} }
  try { const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/); if (m) return m[0]; } catch (_) {}
  return '';
};

/* ── Helpers ─────────────────────────────────────────────────────── */

// Resolve image/video bytes via the backend proxy. Backend caches to local
// disk and silently refreshes via RapidAPI when Meta's signed CDN URLs expire
// (~24-48h), so historical days don't render broken images.
function mediaProxy(adArchiveId, pageDbId, kind, idx) {
  if (!adArchiveId || !pageDbId) return null;
  const qs = new URLSearchParams({ ad: String(adArchiveId), page: String(pageDbId), kind, idx: String(idx) });
  return `${RADAR_API}/media-proxy?${qs.toString()}`;
}

function pickCardImages(snapshot) {
  if (!snapshot) return [];
  const urls = [];
  if (Array.isArray(snapshot.cards)) {
    snapshot.cards.forEach(c => {
      const u = c?.resized_image_url || c?.original_image_url;
      if (u) urls.push(u);
    });
  }
  if (Array.isArray(snapshot.images)) {
    snapshot.images.forEach(u => {
      if (typeof u === 'string') urls.push(u);
      else if (u?.url) urls.push(u.url);
    });
  }
  return [...new Set(urls)].slice(0, 12);
}

function pickCardVideos(snapshot) {
  if (!snapshot) return [];
  const out = [];
  const seen = new Set();
  const push = (v) => {
    if (!v || typeof v !== 'object') return;
    const hd = v.video_hd_url || v.video_url_hd || null;
    const sd = v.video_sd_url || v.video_url_sd || v.video_url || null;
    const url = hd || sd;
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push({ url, preview: v.video_preview_image_url || v.preview_image_url || null });
  };
  if (Array.isArray(snapshot.videos)) snapshot.videos.forEach(push);
  if (Array.isArray(snapshot.cards)) snapshot.cards.forEach(push);
  return out.slice(0, 6);
}

const fmtSyncDate = (v) => {
  if (!v) return 'Never';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  const now = new Date();
  const diffMs = now - d;
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const fmtDateLabel = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

/* ── Ad Card (Facebook style) ────────────────────────────────────── */

function FbAdCard({ ad }) {
  // ad is either raw_json (from DB) or direct API shape
  const row = ad;
  const snap = row.snapshot || {};
  const images = ad._dbImages || pickCardImages(snap);
  const videosFromDb = Array.isArray(ad._dbVideos)
    ? ad._dbVideos.map(v => ({ url: v.hd_url || v.sd_url, preview: v.preview_image_url || null })).filter(v => v.url)
    : null;
  const videos = videosFromDb && videosFromDb.length ? videosFromDb : pickCardVideos(snap);
  const hasMedia = images.length > 0 || videos.length > 0;
  const adArchiveId = row.ad_archive_id;
  const pageDbId = ad._pageDbId;
  const imgSrc = (i) => mediaProxy(adArchiveId, pageDbId, 'image', i) || images[i] || '';
  const videoSrc = (i) => mediaProxy(adArchiveId, pageDbId, 'video', i) || videos[i]?.url || '';
  const videoPosterSrc = (i) => mediaProxy(adArchiveId, pageDbId, 'videoPreview', i) || videos[i]?.preview || undefined;
  const title =
    snap.title ||
    (Array.isArray(snap.cards) && snap.cards[0]?.title) ||
    snap.body?.text ||
    row.page_name || 'Ad';
  const bodyShort =
    (typeof snap.body?.text === 'string' && snap.body.text.slice(0, 160)) ||
    (Array.isArray(snap.cards) && snap.cards[0]?.body?.slice(0, 160)) || '';
  const linkUrl = snap.link_url || row.url || (Array.isArray(snap.cards) && snap.cards[0]?.link_url);
  const libUrl = row.url || (row.ad_archive_id ? `https://www.facebook.com/ads/library?id=${row.ad_archive_id}` : null);
  const platforms = Array.isArray(row.publisher_platform) ? row.publisher_platform.join(', ') : (typeof row.publisher_platform === 'string' ? row.publisher_platform : '');
  let linkDisplay = null;
  if (linkUrl) {
    const s = linkUrl.replace(/^https?:\/\//, '');
    linkDisplay = s.length > 52 ? `${s.slice(0, 52)}...` : s;
  }
  const igHandle = row.page_name ? String(row.page_name).replace(/\s+/g, '').slice(0, 22) : 'page';

  return (
    <article className="group rounded-2xl border border-slate-200/90 bg-white shadow-sm hover:shadow-md hover:border-slate-300/80 transition-all duration-200 overflow-hidden flex flex-col h-full w-full">
      {/* Advertiser header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-slate-100 bg-gradient-to-b from-slate-50/90 to-white">
        <div className="w-9 h-9 rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden shrink-0">
          {images[0] ? (
            <img src={imgSrc(0)} alt="" className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-100">
              <Megaphone className="w-4 h-4 text-slate-400" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Ad</span>
            {platforms && <span className="rounded-md bg-slate-900/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{platforms}</span>}
          </div>
          <span className="text-xs font-semibold text-slate-900 truncate block">{igHandle}</span>
        </div>
        {libUrl && (
          <a href={libUrl} target="_blank" rel="noopener noreferrer"
            className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#0668E1]"
            title="Open in Meta Ads Library" onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Video (if present) — shown above images */}
      {videos.length > 0 && (
        <div className="bg-black aspect-video overflow-hidden">
          <video
            src={videoSrc(0)}
            poster={videoPosterSrc(0)}
            className="w-full h-full object-contain"
            controls
            muted
            playsInline
            preload="metadata"
            referrerPolicy="no-referrer"
          />
        </div>
      )}

      {/* Images grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-200/60 min-h-[120px]">
        {!hasMedia && (
          <div className="col-span-full flex items-center justify-center text-slate-400 p-8 bg-white">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        {images.slice(0, 6).map((src, i) => (
          <a key={`${src}-${i}`} href={src} target="_blank" rel="noopener noreferrer"
            className="relative aspect-square bg-slate-100 block overflow-hidden group">
            <img src={imgSrc(i)} alt="" className="w-full h-full object-cover group-hover:opacity-95 transition-opacity"
              loading="lazy" referrerPolicy="no-referrer" />
          </a>
        ))}
      </div>
      {images.length > 6 && (
        <p className="text-[10px] text-slate-500 px-2 py-1.5 bg-slate-50/90 border-t border-slate-100">
          +{images.length - 6} more images
        </p>
      )}

      {/* Content */}
      <div className="px-3 pb-3 pt-2 flex-1 flex flex-col gap-1.5 bg-white">
        <h3 className="text-xs font-semibold text-neutral-900 line-clamp-2 leading-snug">{title}</h3>
        {bodyShort && <p className="text-[11px] text-slate-600 line-clamp-3 leading-relaxed">{bodyShort}</p>}
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-slate-500 mt-auto pt-1">
          {row.ad_archive_id && <span>ID: {row.ad_archive_id}</span>}
          {row.start_date_string || row.start_date ? <span>{row.start_date_string || row.start_date}</span> : null}
        </div>
        {linkUrl && (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-[#00376B] truncate hover:underline block font-medium" title={linkUrl}>
            {linkDisplay}
          </a>
        )}
      </div>
    </article>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════════════ */

export default function FacebookAdsLibraryRapidLab() {
  /* ── Radar state ───────────────────────────────────────────────── */
  const [savedPages, setSavedPages] = useState([]);
  const [selectedPageDbId, setSelectedPageDbId] = useState(null);
  const [radarAds, setRadarAds] = useState([]);
  const [radarLoading, setRadarLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState('');
  const [addHits, setAddHits] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addingToRadar, setAddingToRadar] = useState(false);
  const addAbortRef = useRef(null);
  const addGenRef = useRef(0);
  const [deleteState, setDeleteState] = useState({ open: false, page: null });
  const [filterQuery, setFilterQuery] = useState('');
  const radarGridRef = useRef(null);

  /* ── Date filter state ─────────────────────────────────────────── */
  const [radarDates, setRadarDates] = useState([]);
  const [radarSelectedDate, setRadarSelectedDate] = useState(null);

  /* ── Load saved pages on mount ──────────────────────────────────── */
  const loadSavedPages = useCallback(async () => {
    const brandId = getBrandId();
    if (!brandId) return;
    try {
      const resp = await fetch(`${RADAR_API}/pages`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) return;
      const data = await resp.json();
      setSavedPages(data?.pages || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadSavedPages(); }, [loadSavedPages]);

  const filteredSaved = savedPages.filter(p =>
    (p.page_name || '').toLowerCase().includes(filterQuery.toLowerCase()) ||
    (p.page_id || '').toLowerCase().includes(filterQuery.toLowerCase()) ||
    (p.ig_username || '').toLowerCase().includes(filterQuery.toLowerCase())
  );

  /* ── Load ads for a specific date ──────────────────────────────── */
  const loadAdsForDate = useCallback(async (pageDbId, date) => {
    const brandId = getBrandId();
    if (!brandId || !pageDbId) return;
    setRadarLoading(true);
    setRadarAds([]);
    try {
      const qs = date ? `?date=${date}` : '';
      const resp = await fetch(`${RADAR_API}/page/${pageDbId}/ads${qs}`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const items = (data?.ads || []).map(a => {
          if (a.raw_json) {
            const row = { ...a.raw_json, _db: a, _pageDbId: pageDbId };
            // If raw_json doesn't have images but DB has snapshot_images, inject them
            if (a.snapshot_images && Array.isArray(a.snapshot_images)) {
              row._dbImages = a.snapshot_images;
            }
            if (a.snapshot_videos && Array.isArray(a.snapshot_videos)) {
              row._dbVideos = a.snapshot_videos;
            }
            return row;
          }
          return { ...a, _pageDbId: pageDbId };
        });
        setRadarAds(items);
      }
    } catch (_) {}
    finally { setRadarLoading(false); }
  }, []);

  /* ── Select saved page → load dates + ads ──────────────────────── */
  const selectRadarPage = useCallback(async (pg) => {
    const newId = selectedPageDbId === pg.id ? null : pg.id;

    if (!newId) { setSelectedPageDbId(null); setRadarAds([]); setRadarDates([]); setRadarSelectedDate(null); return; }
    setSelectedPageDbId(newId);
    setRadarLoading(true);
    setRadarAds([]);
    setRadarDates([]);
    setRadarSelectedDate(null);

    const brandId = getBrandId();
    try {
      // Fetch available dates
      const datesResp = await fetch(`${RADAR_API}/page/${pg.id}/dates`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      let dates = [];
      if (datesResp.ok) {
        const datesData = await datesResp.json();
        dates = datesData?.dates || [];
      }
      setRadarDates(dates);

      // Load ads for most recent date
      const latestDate = dates.length > 0 ? dates[0] : null;
      const dateStr = latestDate ? (typeof latestDate === 'string' ? latestDate.split('T')[0] : new Date(latestDate).toISOString().split('T')[0]) : null;
      setRadarSelectedDate(dateStr);

      const qs = dateStr ? `?date=${dateStr}` : '';
      const resp = await fetch(`${RADAR_API}/page/${pg.id}/ads${qs}`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const items = (data?.ads || []).map(a => {
          if (a.raw_json) {
            const row = { ...a.raw_json, _db: a, _pageDbId: pg.id };
            if (a.snapshot_images && Array.isArray(a.snapshot_images)) row._dbImages = a.snapshot_images;
            if (a.snapshot_videos && Array.isArray(a.snapshot_videos)) row._dbVideos = a.snapshot_videos;
            return row;
          }
          return { ...a, _pageDbId: pg.id };
        });
        setRadarAds(items);
      }
    } catch (_) {}
    finally {
      setRadarLoading(false);
      setTimeout(() => radarGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [selectedPageDbId]);

  /* ── Date change handler ───────────────────────────────────────── */
  const handleDateChange = useCallback((dateStr) => {
    if (!selectedPageDbId || dateStr === radarSelectedDate) return;
    setRadarSelectedDate(dateStr);
    loadAdsForDate(selectedPageDbId, dateStr);
  }, [selectedPageDbId, radarSelectedDate, loadAdsForDate]);

  /* ── Add page to radar ─────────────────────────────────────────── */
  const addToRadar = useCallback(async (row) => {
    const brandId = getBrandId();
    if (!brandId) { toast.error('Brand not selected'); return; }
    setAddingToRadar(true);
    setAddError('');
    try {
      const resp = await fetch(`${RADAR_API}/page/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({
          pageId: String(row.page_id),
          pageName: row.name || null,
          pageImage: row.image_uri || null,
          igUsername: row.ig_username || null,
          brandId,
          country: 'TR',
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || 'Failed to add page');
      toast.success(`${row.name || 'Page'} saved`);
      await loadSavedPages();
      setAddInput(''); setAddHits([]); setIsAddOpen(false);
    } catch (e) {
      const msg = e?.message || 'Failed to add';
      toast.error(msg);
      setAddError(msg);
    } finally { setAddingToRadar(false); }
  }, [loadSavedPages]);

  /* ── Debounced search inside Add modal ─────────────────────────── */
  useEffect(() => {
    if (!isAddOpen) return;
    const q = addInput.trim();
    if (q.length < MIN_Q) { addAbortRef.current?.abort(); addGenRef.current++; setAddHits([]); setAddSearching(false); return; }
    setAddSearching(true);
    const t = setTimeout(async () => {
      addAbortRef.current?.abort(); const ac = new AbortController(); addAbortRef.current = ac; const g = ++addGenRef.current;
      try {
        const url = `${API}/search/companies?${new URLSearchParams({ query: q, country: 'TR' })}`;
        const resp = await fetch(url, { credentials: 'include', signal: ac.signal });
        if (g !== addGenRef.current) return;
        const json = await resp.json();
        const rows = Array.isArray(json?.searchResults) ? json.searchResults : [];
        setAddHits(rows);
      } catch (e) { if (e.name === 'AbortError') return; if (g === addGenRef.current) setAddHits([]); }
      finally { if (g === addGenRef.current) setAddSearching(false); }
    }, DEBOUNCE);
    return () => { clearTimeout(t); addAbortRef.current?.abort(); };
  }, [addInput, isAddOpen]);

  /* ── Delete page from radar ────────────────────────────────────── */
  const performDelete = useCallback(async () => {
    if (!deleteState?.page) return;
    const brandId = getBrandId();
    if (!brandId) return;
    try {
      const resp = await fetch(`${RADAR_API}/page/${deleteState.page.id}`, {
        method: 'DELETE',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');
      setSavedPages(prev => prev.filter(p => p.id !== deleteState.page.id));
      if (selectedPageDbId === deleteState.page.id) { setSelectedPageDbId(null); setRadarAds([]); setRadarDates([]); setRadarSelectedDate(null); }
      toast.success('Page removed');
      setDeleteState({ open: false, page: null });
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  }, [deleteState, selectedPageDbId]);

  /* ── Manual sync ─────────────────────────────────────────────────── */
  const syncPage = useCallback(async (pg) => {
    const brandId = getBrandId();
    if (!brandId) return;
    setSyncingId(pg.id);
    try {
      const resp = await fetch(`${RADAR_API}/page/${pg.id}/sync`, {
        method: 'POST',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      const data = await resp.json();
      if (resp.ok && data?.ok) {
        toast.success(`${pg.page_name}: ${data.stored} ads synced`);
        await loadSavedPages();
        if (selectedPageDbId === pg.id) selectRadarPage(pg);
      } else {
        toast.error(data?.error || 'Sync failed');
      }
    } catch (e) { toast.error(e?.message || 'Sync failed'); }
    finally { setSyncingId(null); }
  }, [selectedPageDbId, selectRadarPage, loadSavedPages]);

  const selectedPage = savedPages.find(p => p.id === selectedPageDbId);

  return (
    <div className="p-6 bg-slate-50/50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* ═══ Saved Pages Table ═══ */}
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white mb-5">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/facebook.png" alt="Facebook" className="h-7" />
              <span className="text-base font-semibold text-slate-900">Ads Library</span>
            </div>
            <div className="flex items-center gap-2">
              {savedPages.length > 3 && (
                <input value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} placeholder="Filter..."
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors w-40" />
              )}
              <button className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors flex items-center gap-1.5"
                onClick={() => { setIsAddOpen(true); setAddError(''); setAddInput(''); }}>
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '40%' }}>Page</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Ads</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Country</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '15%' }}>Last Sync</th>
                  <th className="px-4 py-2.5" style={{ width: '15%' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSaved.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                    {savedPages.length === 0 ? 'No pages tracked yet. Click "+ Add" to start.' : 'No match.'}
                  </td></tr>
                )}
                {filteredSaved.map((pg) => {
                  const isSelected = selectedPageDbId === pg.id;
                  return (
                    <tr key={pg.id}
                      className={`cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${isSelected ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}`}
                      onClick={() => selectRadarPage(pg)}>
                      <td className="px-4 py-2.5 text-sm align-middle">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-semibold shrink-0 overflow-hidden">
                            {pg.page_image ? (
                              <img src={pg.page_image} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              (pg.page_name || '?')[0].toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate text-slate-900">{pg.page_name || '(No name)'}</div>
                            <div className="text-[10px] text-slate-400 font-mono">
                              {pg.ig_username ? `@${pg.ig_username}` : pg.page_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">
                        {Number(pg.total_ads || 0).toLocaleString('en-US')}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-600">{pg.country || 'TR'}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-slate-500">{fmtSyncDate(pg.last_synced_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="inline-flex p-1.5 rounded hover:bg-blue-50 text-slate-300 hover:text-blue-500 transition-colors"
                            title="Sync now"
                            onClick={(e) => { e.stopPropagation(); syncPage(pg); }}>
                            <RefreshCw className={`w-3.5 h-3.5 ${syncingId === pg.id ? 'animate-spin' : ''}`} />
                          </button>
                          <button className="inline-flex p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                            title="Remove"
                            onClick={(e) => { e.stopPropagation(); setDeleteState({ open: true, page: pg }); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ Ad Grid — from radar (DB) ═══ */}
        {selectedPageDbId && (
          <div ref={radarGridRef}>
            {/* Header bar */}
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold overflow-hidden">
                  {selectedPage?.page_image ? (
                    <img src={selectedPage.page_image} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    (selectedPage?.page_name || '?')[0].toUpperCase()
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{selectedPage?.page_name}</div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    {selectedPage?.ig_username ? `@${selectedPage.ig_username}` : selectedPage?.page_id}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Date filter */}
                {radarDates.length > 0 && (
                  <div className="relative">
                    <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <select
                      value={radarSelectedDate || ''}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="pl-8 pr-7 py-1.5 text-[12px] font-medium border border-slate-200 rounded-lg bg-slate-50 hover:bg-white focus:bg-white focus:border-slate-300 outline-none transition-colors appearance-none cursor-pointer text-slate-700"
                    >
                      {radarDates.map((d) => {
                        const ds = typeof d === 'string' ? d.split('T')[0] : new Date(d).toISOString().split('T')[0];
                        return <option key={ds} value={ds}>{fmtDateLabel(ds)}</option>;
                      })}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                )}

                {radarAds.length > 0 && (
                  <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {radarAds.length} ads
                  </span>
                )}
                <button
                  className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                  disabled={syncingId === selectedPageDbId}
                  onClick={() => selectedPage && syncPage(selectedPage)}>
                  <RefreshCw className={`w-3 h-3 ${syncingId === selectedPageDbId ? 'animate-spin' : ''}`} />
                  Sync Now
                </button>
                <button onClick={() => { setSelectedPageDbId(null); setRadarAds([]); setRadarDates([]); setRadarSelectedDate(null); }}
                  className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {radarLoading && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center gap-2 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Loading archived ads...</span>
              </div>
            )}

            {!radarLoading && radarAds.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {radarAds.map((ad, i) => (
                  <FbAdCard key={`${ad.ad_archive_id || i}-${i}`} ad={ad} />
                ))}
              </div>
            )}

            {!radarLoading && radarAds.length === 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                <p className="text-sm text-slate-400">No ads archived yet. Click "Sync Now" to fetch ads.</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ═══ Add Page Modal (search + dropdown) ═══ */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setIsAddOpen(false); setAddHits([]); setAddInput(''); }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            style={{ animation: 'popIn 0.2s ease-out' }} onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900">Add Page to Radar</span>
              <button onClick={() => { setIsAddOpen(false); setAddHits([]); setAddInput(''); }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Search page or brand</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
                    value={addInput}
                    onChange={(e) => { setAddInput(e.target.value); setAddError(''); }}
                    placeholder="Type brand name..."
                    autoFocus
                  />
                  {addSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />}
                </div>

                {addInput.trim().length >= MIN_Q && (
                  <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden max-h-[350px] overflow-y-auto">
                    {addSearching && addHits.length === 0 && (
                      <div className="flex items-center gap-2 px-4 py-4 text-sm text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching Meta Ads Library...</div>
                    )}
                    {!addSearching && addHits.length === 0 && addInput.trim().length >= MIN_Q && (
                      <div className="px-4 py-4 text-sm text-slate-400 text-center">No pages found for "{addInput.trim()}"</div>
                    )}
                    {addHits.map((row) => {
                      const alreadySaved = savedPages.some(p => p.page_id === String(row.page_id));
                      return (
                        <button key={String(row.page_id)} type="button"
                          disabled={addingToRadar || alreadySaved}
                          onClick={() => { if (!alreadySaved) addToRadar(row); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-slate-50 last:border-0 transition-colors ${alreadySaved ? 'bg-slate-50 cursor-default' : 'hover:bg-blue-50 cursor-pointer'}`}>
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                            {row.image_uri ? (
                              <img src={row.image_uri} alt="" className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                            ) : (
                              <Instagram className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-slate-900 block truncate">{row.name || '(No name)'}</span>
                            <div className="flex items-center gap-2 text-[10px] text-slate-400">
                              {row.ig_username && <span>@{row.ig_username}</span>}
                              <span className="font-mono">{row.page_id}</span>
                              {row.ig_followers != null && <span>{Number(row.ig_followers).toLocaleString('en-US')} followers</span>}
                            </div>
                          </div>
                          {alreadySaved ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0">Saved</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 shrink-0 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {addError && <p className="text-xs text-red-500">{addError}</p>}
              {addingToRadar && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </div>
              )}
            </div>
          </div>
          <style>{`@keyframes popIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }`}</style>
        </div>
      )}

      {/* ═══ Delete Confirmation Modal ═══ */}
      {deleteState.open && deleteState.page && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDeleteState({ open: false, page: null })}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden"
            style={{ animation: 'popIn 0.2s ease-out' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-slate-900">Remove page?</p>
              <p className="text-sm text-slate-600">
                <strong>{deleteState.page.page_name}</strong> will be removed from your radar. Archived ads will remain in the database.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                  onClick={() => setDeleteState({ open: false, page: null })}>Cancel</button>
                <button className="px-4 py-1.5 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
                  onClick={performDelete}>Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
